import React from 'react'
import { Button, Spin } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'

import type { LinkedAccountItem } from '../../types/linkedAccount'
import { sortLinkedAccounts } from '../../util/linkedAccount/linkedAccountDisplay'
import LinkedAccountCard from './LinkedAccountCard'

/**
 * Danh sách thẻ tài khoản liên kết + hai trạng thái rỗng (SP-005, Task 3).
 *
 * Luôn sắp xếp bằng `sortLinkedAccounts` trước khi render — không tin thứ tự API trả về.
 *
 * DOM hook cho các task sau:
 * - `.linked-account-list__empty-text` — câu rỗng (một trong hai câu, tuỳ `isOwnView`).
 * - `.linked-account-list__add-btn` — nút "Thêm tài khoản" (xuất hiện ở CẢ header lẫn khối rỗng
 *   tuỳ ngữ cảnh, luôn cùng một class để test/CSS không phải phân biệt).
 * - `.linked-account-list__title` — tiêu đề "Danh sách liên kết", LUÔN hiện (cả khi rỗng — Figma
 *   `26:8142`/`26:8320` giữ hàng tiêu đề, khối rỗng nằm BÊN DƯỚI).
 * - `.linked-account-list__loading` — vùng giữ chỗ khi tải LẦN ĐẦU (chưa có thẻ nào): spinner giữa một
 *   khoảng cao bằng khối rỗng, nằm DƯỚI hàng tiêu đề. Tải lại khi đã có thẻ thì `Spin` phủ mờ các thẻ.
 *   Không bọc cả hàng tiêu đề trong `Spin` — spinner sẽ chen giữa tiêu đề và nút Thêm, cả hàng bị mờ.
 */

export interface LinkedAccountListProps {
  items: readonly LinkedAccountItem[]
  loading: boolean
  canManage: boolean
  /** true = đang xem bản ghi của CHÍNH MÌNH ⇒ dùng câu rỗng thứ hai, không có nút Thêm. */
  isOwnView: boolean
  onAdd: () => void
  onRevealPassword: (item: LinkedAccountItem) => void
  onEdit: (item: LinkedAccountItem) => void
  onDelete: (item: LinkedAccountItem) => void
}

const LIST_TITLE = 'Danh sách liên kết'
const ADD_BUTTON_LABEL = 'Thêm tài khoản'
const EMPTY_TEXT_OTHER_VIEW = 'Tài khoản này chưa được cấp tài khoản liên kết nào.'
const EMPTY_TEXT_OWN_VIEW = 'Bạn chưa được cấp tài khoản liên kết nào. Liên hệ quản trị viên để được hỗ trợ.'

/** Hàng tiêu đề dùng chung — `LinkedAccountTab` dựng lại nó cho trạng thái tải lỗi. */
export const LinkedAccountListHeader = ({ action = null }: { action?: React.ReactNode }): JSX.Element => (
  <div className="linked-account-list__header">
    <span className="linked-account-list__title">{LIST_TITLE}</span>
    {action}
  </div>
)

const LinkedAccountList = ({
  items, loading, canManage, isOwnView, onAdd, onRevealPassword, onEdit, onDelete,
}: LinkedAccountListProps): JSX.Element => {
  const sortedItems = sortLinkedAccounts(items)
  const hasItems = sortedItems.length > 0
  const showEmptyState = !loading && !hasItems
  // `isOwnView` luôn thắng: xem bản ghi của CHÍNH MÌNH thì không có nút Thêm, kể cả khi `canManage`
  // vô tình là `true` — component tự gác, không dựa vào việc nơi gọi không bao giờ truyền tổ hợp đó.
  const showAddButton = !isOwnView && canManage

  const addButton = (
    <Button
      type="primary"
      size="large"
      icon={<PlusOutlined />}
      className="linked-account-list__add-btn"
      onClick={onAdd}
    >
      {ADD_BUTTON_LABEL}
    </Button>
  )

  return (
    <div className="linked-account-list">
      <LinkedAccountListHeader action={showAddButton ? addButton : null} />
      {loading && !hasItems && (
        <div className="linked-account-list__loading">
          <Spin />
        </div>
      )}
      {showEmptyState && (
        // Figma: khối nền xám, vòng tròn trắng chứa icon — dấu `+` khi quản trị xem tài khoản
        // khác (`26:8316`), dấu `×` khi người dùng xem của chính mình (`26:8343`). Icon chỉ để
        // minh hoạ, KHÔNG bấm được: nút Thêm thật nằm ở hàng tiêu đề ngay trên.
        <div className={`linked-account-list__empty linked-account-list__empty--${isOwnView ? 'own' : 'other'}`}>
          <span className="linked-account-list__empty-icon" aria-hidden>
            {isOwnView ? <CloseOutlined /> : <PlusOutlined />}
          </span>
          <p className="linked-account-list__empty-text">
            {isOwnView ? EMPTY_TEXT_OWN_VIEW : EMPTY_TEXT_OTHER_VIEW}
          </p>
        </div>
      )}
      {hasItems && (
        <Spin spinning={loading}>
          <div className="linked-account-list__items">
            {sortedItems.map((item) => (
              <LinkedAccountCard
                key={item.id}
                item={item}
                canManage={canManage}
                onRevealPassword={onRevealPassword}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </Spin>
      )}
    </div>
  )
}

export default LinkedAccountList
